import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Spin } from "@arco-design/web-react";
import { EmailEditorProvider, EmailEditor } from "easy-email-editor";
import { StandardLayout } from "easy-email-extensions";
import { BlockManager, BasicType, AdvancedType, JsonToMjml } from "easy-email-core";
import mjml from "mjml-browser";

import "easy-email-editor/lib/style.css";
import "easy-email-extensions/lib/style.css";
import { MERGE_TAGS, confirmButtonPayload, flowLinkFromEvent, mergeTagGenerate as generateTag, previewHtml } from "./campaignTags";

// EasyEmailEditor — reusable wrapper around Easy Email (MJML visual editor).
//
// Contract with the parent (variant editors):
//   - `initialDesign`: the persisted Easy Email design (the `editor_design`
//     blob: { content, subject, subTitle }), or null/undefined for a new variant.
//   - `mergeTags`: tree of personalization fields for the text toolbar's merge
//     tag menu (default: the Contact fields). A picked path like Contact.Name
//     inserts `{{ .Contact.Name }}` — the Go text/template syntax the sender
//     expands at send time.
//   - `confirmLink`: add a "Campaign" palette group with a Confirm button
//     already linked to the double opt-in confirm URL.
//   - `onFlowLink`: called when a campaign flow link (confirm / unsubscribe /
//     tracked) is clicked in the preview — the editor offers Test drive, since
//     those links only work per recipient at send time.
//   - ref.getContent(): { html, design } — call on SAVE. `design` goes to
//     `editor_design` (re-editable); `html` goes to `mail_html_template`.
//
// Easy Email's source of truth is its own block-tree JSON, so this is for new /
// design-backed variants; legacy raw-HTML variants use the Source editor.

// Block palette shown in the left panel. Mirrors Easy Email's standard config.
const CATEGORIES = [
  {
    label: "Content",
    active: true,
    blocks: [
      { type: AdvancedType.TEXT },
      { type: AdvancedType.IMAGE, payload: { attributes: { padding: "0px 0px 0px 0px" } } },
      { type: AdvancedType.BUTTON },
      { type: AdvancedType.SOCIAL },
      { type: AdvancedType.DIVIDER },
      { type: AdvancedType.SPACER },
      { type: AdvancedType.HERO },
      { type: AdvancedType.WRAPPER },
    ],
  },
  {
    label: "Layout",
    active: true,
    displayType: "column",
    blocks: [
      { title: "1 column", payload: [["100%"]] },
      { title: "2 columns (50/50)", payload: [["50%", "50%"]] },
      { title: "2 columns (33/67)", payload: [["33%", "67%"]] },
      { title: "2 columns (67/33)", payload: [["67%", "33%"]] },
      { title: "3 columns", payload: [["33.33%", "33.33%", "33.33%"]] },
      { title: "4 columns", payload: [["25%", "25%", "25%", "25%"]] },
    ],
  },
];

// Palette group for double opt-in signup emails: a button already linked to the
// confirm URL, so nobody has to paste a template tag.
const CAMPAIGN_CATEGORY = {
  label: "Campaign",
  active: true,
  blocks: [{ type: AdvancedType.BUTTON, title: "Confirm button", payload: confirmButtonPayload() }],
};

// A blank page block for brand-new variants.
function blankTemplate() {
  return {
    subject: "",
    subTitle: "",
    content: BlockManager.getBlockByType(BasicType.PAGE).create({}),
  };
}

// Compile an IEmailTemplate's block tree to final, send-ready HTML.
function designToHtml(values) {
  const mjmlString = JsonToMjml({
    data: values.content,
    mode: "production",
    context: values.content,
  });
  return mjml(mjmlString, { validationLevel: "soft" }).html;
}

const EasyEmailEditor = forwardRef(function EasyEmailEditor(
  { initialDesign, mergeTags = MERGE_TAGS, confirmLink = false, onFlowLink = null, height = "600px" },
  ref
) {
  // Easy Email renders its <Form> with subscription={{submitting, pristine}}, so
  // the children render prop does NOT re-run on value edits — reading
  // formState.values there is stale. Instead capture the final-form API and read
  // getState().values on demand, which always reflects the live edits.
  const formApiRef = useRef(null);

  // `data` seeds final-form once; compute it a single time from the incoming
  // design. The parent must only mount this once its design has loaded (or key
  // it), since changing `data` afterwards won't reset the form.
  const [initialValues] = useState(() =>
    initialDesign && initialDesign.content ? initialDesign : blankTemplate()
  );

  useImperativeHandle(
    ref,
    () => ({
      getContent: () => {
        const api = formApiRef.current;
        const values = api ? api.getState().values : initialValues;
        return { html: designToHtml(values), design: values };
      },
    }),
    [initialValues]
  );

  const mergeTagGenerate = useMemo(() => generateTag, []);
  // In the preview, placeholder links go nowhere; with onFlowLink, clicking a
  // campaign flow link (tagged by previewHtml) calls it instead.
  const beforePreview = useMemo(() => (html) => previewHtml(html, !!onFlowLink), [onFlowLink]);
  useEffect(() => {
    if (!onFlowLink) return undefined;
    const onClick = (e) => {
      if (flowLinkFromEvent(e)) {
        e.preventDefault();
        e.stopPropagation();
        onFlowLink();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [onFlowLink]);
  const categories = useMemo(() => (confirmLink ? [CAMPAIGN_CATEGORY, ...CATEGORIES] : CATEGORIES), [confirmLink]);

  // Easy Email's canvas ("Page") takes a beat to build after mount, and the
  // library exposes no "ready" event — so cover the visible init gap with a
  // spinner overlay instead of a blank panel. Revealed after a short settle;
  // tune the delay if it uncovers the editor too early/late.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ position: "relative", height }}>
      {!ready && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--color-bg-2)",
          }}
        >
          <Spin tip="Loading editor…" size={40} />
        </div>
      )}
      <EmailEditorProvider
        data={initialValues}
        height={height}
        mergeTags={mergeTags}
        mergeTagGenerate={mergeTagGenerate}
        // Template links (the confirm button) only become real at send time;
        // in the preview they'd load the app itself, so make them inert.
        onBeforePreview={beforePreview}
        onSubmit={() => {}}
      >
        {(formState, formApi) => {
          // Capture the form API (stable) so getContent() can read live values via
          // getState() regardless of the provider's render subscription.
          formApiRef.current = formApi;
          return (
            <StandardLayout categories={categories} showSourceCode>
              <EmailEditor />
            </StandardLayout>
          );
        }}
      </EmailEditorProvider>
    </div>
  );
});

export default EasyEmailEditor;
